import React from "react";
import { AppHeader } from "./CommunitySelection";
import { currency } from "../lib/constants";
import { useLang } from "../lib/i18n";
import { Users, Wallet, ArrowRight, Info } from "lucide-react";

export const BudgetIntro = ({ session, onBegin, quick = false }) => {
  const { t } = useLang();
  const { family, situation, community_name } = session;
  const disc = situation.discretionary;

  return (
    <div className="min-h-screen">
      <AppHeader subtitle={community_name} />
      <div className="max-w-3xl mx-auto px-5 sm:px-6 py-10 sm:py-12">
        <div className="animate-rise">
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-vin/70 mb-3">
            {t("Your neighbor")}
          </p>
          <h2 className="font-display text-3xl sm:text-4xl font-semibold text-ink leading-tight">
            {family.label}
          </h2>
          <p className="mt-3 text-base text-ink-soft">{t("Living in")} {community_name}.</p>
        </div>

        <div className="mt-7 grid grid-cols-1 gap-4">
          <div className="bg-white rounded-lg border border-[var(--line)] p-6 grid sm:grid-cols-2 gap-5">
            <div className="flex gap-3">
              <Users className="w-5 h-5 text-vin shrink-0 mt-0.5" strokeWidth={1.75} />
              <div>
                <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-vin-dark/70">{t("Household")}</div>
                <div className="text-sm text-ink mt-1">{family.household}</div>
              </div>
            </div>
            <div className="flex gap-3">
              <Wallet className="w-5 h-5 text-vin shrink-0 mt-0.5" strokeWidth={1.75} />
              <div>
                <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-vin-dark/70">{t("Income")}</div>
                <div className="text-sm text-ink mt-1">{family.income_source}</div>
              </div>
            </div>
            {family.health_note && (
              <div className="flex gap-3 sm:col-span-2">
                <Info className="w-5 h-5 text-vin shrink-0 mt-0.5" strokeWidth={1.75} />
                <div>
                  <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-vin-dark/70">{t("Also carrying")}</div>
                  <div className="text-sm text-ink mt-1 capitalize">{family.health_note}</div>
                </div>
              </div>
            )}
          </div>

          <div className="bg-white rounded-lg border border-[var(--line)] overflow-hidden">
            <div className="px-6 pt-6 pb-4">
              <h3 className="font-display text-lg font-semibold text-ink">{t("A typical month")}</h3>
              <p className="text-sm text-ink-soft mt-1">
                {situation.subsidized ? t("Housing is subsidized, yet ") : ""}{t("nearly every dollar is already spoken for.")}
              </p>
            </div>
            <div className="px-6 flex items-center justify-between py-3 border-t border-[var(--line)]">
              <span className="text-sm font-medium text-ink">{t("Money coming in")}</span>
              <span className="font-display font-semibold text-vin" data-testid="situation-income">
                {currency(situation.income)}
              </span>
            </div>
            <div className="border-t border-[var(--line)]">
              {Object.entries(situation.breakdown)
                .filter(([, v]) => v > 0)
                .map(([k, v]) => (
                  <div key={k} className="px-6 flex items-center justify-between py-2.5 text-sm">
                    <span className="text-ink-soft">{t(k)}</span>
                    <span className="text-ink tabular-nums">{currency(v)}</span>
                  </div>
                ))}
            </div>
            <div className="px-6 flex items-center justify-between py-3 border-t border-[var(--line)] bg-slate-50">
              <span className="text-sm font-medium text-ink">{t("Essentials")}</span>
              <span className="font-medium text-ink tabular-nums">{currency(situation.essentials)}</span>
            </div>
            <div className="px-6 flex items-center justify-between py-4 border-t-2 border-vin/20 bg-vin/5">
              <span className="text-sm font-semibold text-ink">{t("Left for everything else")}</span>
              <span
                data-testid="situation-remaining"
                className={`font-display text-xl font-semibold ${disc < 0 ? "text-[#8E2929]" : "text-vin"}`}
              >
                {currency(disc)}
              </span>
            </div>
          </div>

          <p className="text-[11px] text-ink-soft leading-relaxed px-1" data-testid="budget-data-note">
            <Info className="w-3 h-3 inline-block mr-1 -mt-0.5" strokeWidth={1.75} />
            {t("Income and costs are based on regional wage & cost-of-living data for this community.")}
          </p>

          <div className="bg-vin/5 border border-vin/15 rounded-lg p-5 text-sm text-vin-dark leading-relaxed">
            {disc < 0
              ? (quick
                  ? t("This family already spends more than it takes in — before anything goes wrong. There is no savings and little or no credit left. A single setback can send them down a path with far-reaching consequences.")
                  : t("This family already spends more than it takes in — before anything goes wrong. There is no savings and little or no credit left. Every month is a set of impossible choices."))
              : (quick
                  ? t("There is almost nothing left over, and no savings to fall back on. It won't take much — one unexpected event — to set off consequences that follow them long after.")
                  : t("There is almost nothing left over, and no savings to fall back on. It won't take much — one unexpected event — to force a painful trade-off."))}
          </div>
        </div>

        <button
          data-testid="begin-year-button"
          onClick={onBegin}
          className="mt-8 w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-vin text-white font-semibold px-8 py-4 rounded-full hover:bg-vin-hover transition-colors duration-200"
        >
          {quick ? t("Make the first decision") : t("Begin the year")}
          <ArrowRight className="w-5 h-5" strokeWidth={1.75} />
        </button>
      </div>
    </div>
  );
};

export default BudgetIntro;
